import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useRelease150 } from "./release150";

// Release 1.5.0 (perf, behind the 1.5.0 switch) — Back and Forward return to where the person was on that page, even
// when the page draws its data a moment later; following a link starts at the top of the new page (unless the link
// points at a section, #…). Positions are kept per history entry in this tab only.

export const SCROLL_STORAGE_KEY = "summitops.scroll.v1";
const MAX_ENTRIES = 50;
export const RESTORE_WINDOW_MS = 1500;

function readAll(storage: Storage): Record<string, number> {
  try {
    return JSON.parse(storage.getItem(SCROLL_STORAGE_KEY) ?? "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

export function saveScroll(storage: Storage, key: string, y: number): void {
  const all = readAll(storage);
  delete all[key];
  all[key] = Math.max(0, Math.round(y));
  const keys = Object.keys(all);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete all[k];
  try {
    storage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage full or unavailable: Back simply starts at the top */
  }
}

export function readScroll(storage: Storage, key: string): number | null {
  const y = readAll(storage)[key];
  return typeof y === "number" && Number.isFinite(y) ? y : null;
}

/** Scrolls to `y` as soon as the page is tall enough (its data may still be on its way), for at most 1.5 s. */
export function restoreWhenTall(y: number, win: Window = window): () => void {
  const started = Date.now();
  let frame = 0;
  const attempt = () => {
    const doc = win.document.documentElement;
    const tallEnough = doc.scrollHeight - win.innerHeight >= y;
    if (tallEnough || Date.now() - started > RESTORE_WINDOW_MS) {
      win.scrollTo(0, y);
      return;
    }
    frame = win.requestAnimationFrame(attempt);
  };
  attempt();
  return () => win.cancelAnimationFrame(frame);
}

export function ScrollMemory() {
  const enabled = useRelease150();
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (!enabled || typeof window.history === "undefined") return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, [enabled]);

  // Remember the position of this history entry while the person scrolls.
  useEffect(() => {
    if (!enabled) return;
    let pending = 0;
    const key = location.key;
    const onScroll = () => {
      if (pending) return;
      pending = window.requestAnimationFrame(() => {
        pending = 0;
        saveScroll(window.sessionStorage, key, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (pending) window.cancelAnimationFrame(pending);
    };
  }, [enabled, location.key]);

  // Arriving: Back/Forward restores, a new link starts at the top.
  useEffect(() => {
    if (!enabled) return;
    if (navigationType === "POP") {
      const y = readScroll(window.sessionStorage, location.key);
      if (y !== null && y > 0) return restoreWhenTall(y);
      return;
    }
    if (navigationType === "PUSH" && !location.hash) window.scrollTo(0, 0);
    // location.hash only matters for the entry being arrived at
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, location.key, navigationType]);

  return null;
}
